import $ from "jquery";
import { letter as L, mark as M } from "./shared/unicodePatterns";

export interface DomSplit {
  /** The original left-half element produced by a Readlang boundary split. */
  original: JQuery<HTMLElement>;
  /** The cloned right-half sibling produced by the split. */
  clone: JQuery<HTMLElement>;
}

/**
 * The maximum total number of words to include in the context sentence. The budget is
 * distributed flexibly - if a word is near the start of a sentence, more words will be
 * captured after it (and vice versa), up to this total limit.
 */
export const maxContextWords = 250;

/** Shown instead of translating when the source is the first language */
export const sameLanguageMessage =
  "Can't translate to the same language. Please ensure that you have different " +
  "languages set for your first language and the one you are reading. " +
  "Note: Even when using Readlang in monolingual mode, you still " +
  "need these languages to be different for Readlang to generate the flashcards you will practice with " +
  "later.";

/**
 * Split inline wrapper ancestors so the selection starts exactly at `wordSpan`.
 *
 * A multi-word group that begins mid-`<strong>` (or any other inline wrapper) cannot be
 * extracted as a contiguous sibling list without first cloning the wrapper: the leading
 * siblings of `wordSpan` must stay inside the original left-half wrapper, while `wordSpan`
 * and everything after it moves into a newly created right-half clone. We walk up the tree
 * doing this for each ancestor until we reach `blockRoot`.
 *
 * Example - selecting `data-p="1"` as start under `<p data-readlang-block>`:
 *   before: <p><strong><span data-p="0">very</span> <span data-p="1">big</span></strong></p>
 *   after:  <p><strong><span data-p="0">very</span> </strong>
 *              <strong><span data-p="1">big</span></strong></p>          ← firstNode
 *
 * Each split is recorded so `rejoinDomSplits` can reverse the operation losslessly when
 * the group is later untranslated.
 *
 * Invariant: the returned `firstNode` is a direct child of `blockRoot` whose first leaf is
 * `wordSpan`, so the caller can safely iterate siblings from `firstNode` to extract the
 * fragment without crossing any wrapper boundary.
 *
 * Exported for tests.
 */
export const splitBoundaryBefore = (
  wordSpan: JQuery<HTMLElement>,
  blockRoot: JQuery<HTMLElement>
): { firstNode: JQuery<HTMLElement>; splits: DomSplit[] } => {
  const splits: DomSplit[] = [];
  let current: JQuery<HTMLElement> = wordSpan;

  while (current.parent().length > 0 && !current.parent().is(blockRoot)) {
    const parent = current.parent();
    if (current[0].previousSibling) {
      const clone = $(parent[0].cloneNode(false) as HTMLElement);
      let node: Node | null = current[0];
      while (node) {
        const nextNode: Node | null = node.nextSibling;
        clone[0].appendChild(node);
        node = nextNode;
      }
      parent.after(clone);
      splits.push({ original: parent, clone });
      current = clone;
    } else {
      current = parent;
    }
  }

  return { firstNode: current, splits };
};

/**
 * Mirror of `splitBoundaryBefore` for the end of the range: split inline wrapper ancestors
 * so that `wordSpan` is the last thing inside its chain of wrappers. Trailing siblings move
 * into a new right-half clone; `wordSpan` stays in the original left-half. Returns the
 * top-level node under `blockRoot` whose final leaf is `wordSpan`.
 *
 * Exported for tests.
 */
export const splitBoundaryAfter = (
  wordSpan: JQuery<HTMLElement>,
  blockRoot: JQuery<HTMLElement>
): { lastNode: JQuery<HTMLElement>; splits: DomSplit[] } => {
  const splits: DomSplit[] = [];
  let current: JQuery<HTMLElement> = wordSpan;

  while (current.parent().length > 0 && !current.parent().is(blockRoot)) {
    const parent = current.parent();
    if (current[0].nextSibling) {
      const clone = $(parent[0].cloneNode(false) as HTMLElement);
      let node: Node | null = current[0].nextSibling;
      while (node) {
        const nextNode: Node | null = node.nextSibling;
        clone[0].appendChild(node);
        node = nextNode;
      }
      parent.after(clone);
      splits.push({ original: parent, clone });
      current = parent;
    } else {
      current = parent;
    }
  }

  return { lastNode: current, splits };
};

/**
 * Undo the wrapper cloning performed by splitBoundaryBefore/After, leaving the DOM
 * byte-for-byte identical to its pre-group state.
 *
 * Iteration order matters. The split helpers push innermost-first (they walk up from the
 * word span), so `splits` is ordered inside-out. We walk it in reverse to rejoin
 * outer wrappers first: rejoining an outer wrapper concatenates its split halves, which may
 * in turn put sibling inner halves back next to each other - cleanly setting up the
 * subsequent inner rejoin. Doing it in forward order would still produce a correct final
 * tree in simple cases but leaves transient empty wrappers on deeply nested splits, so we
 * stick to the defensively-correct reverse order.
 */
export const rejoinDomSplits = (splits: DomSplit[]): void => {
  for (let i = splits.length - 1; i >= 0; i--) {
    const { original, clone } = splits[i];
    while (clone[0].firstChild) {
      original[0].appendChild(clone[0].firstChild);
    }
    clone.remove();
  }
};

// Duplicated in spannify
export const wordRegExp = () => new RegExp(`([‘’${L}${M}]+|[0-9]+)`, "gu");

export const wordRegExpWithPunctuation = () =>
  new RegExp(`([‘’${L}${M}]+|[0-9]+|[«»\u201C\u201D\u201E.,?!¡¿])`, "gu");

export const startOfSentencePunctuationCharacters = ["¿", "¡", "«"];

export const sentenceBreakCharacters = [".", "?", "!"];

/**
 * Sentence terminators that end a sentence wherever they appear, without a
 * following space (unlike `.`, which also appears in "3.14" or "e.g."):
 * CJK full-width full stop, exclamation and question marks (and the halfwidth
 * full stop), Devanagari danda and double danda, Arabic question mark, Urdu
 * full stop, Armenian full stop, Ethiopic full stop, Myanmar section and Khmer
 * khan. Thai has no sentence terminator.
 */
export const noSpaceSentenceBreakCharacters = [
  "。",
  "！",
  "？",
  "｡",
  "।",
  "॥",
  "؟",
  "۔",
  "։",
  "።",
  "။",
  "។",
];

/** Matches any sentence terminator, with or without a following space. */
export const sentenceBreakCharacterRegExp = new RegExp(
  `[${[...sentenceBreakCharacters, ...noSpaceSentenceBreakCharacters].join("")}]`
);

export const sentenceBreakRegExp = () =>
  new RegExp(
    [
      ...sentenceBreakCharacters.map((c) => `\\${c}\\s`),
      ...noSpaceSentenceBreakCharacters.map((c) => `\\${c}`),
    ].join("|")
  );

export const lastSentenceBreakRegExp = () => {
  const allBreakCharacters = [
    ...sentenceBreakCharacters,
    ...noSpaceSentenceBreakCharacters,
    "n",
    "r",
  ];
  return new RegExp(
    `(${[...allBreakCharacters.map((c) => `\\${c}`)].join(
      "|"
    )})[^${allBreakCharacters.map((c) => `\\${c}`).join("")}]*$`
  );
};
